// src/pages/FindSwapV2.tsx
import React, { useEffect, useState } from "react";
import "../css/Lp2App.css";
import {
  bootloopClient,
  BootListingMatchDto,
  BootCondition,
  ShoeType,
  conditionLabel,
  shoeTypeLabel,
} from "../api/bootloopClient";
import { Link } from "react-router-dom";

/* ------------------------------------------------------------------
   Config
------------------------------------------------------------------ */

const LS_EMAIL = "bootloop.email";
const LS_POSTCODE = "bootloop.postcode";
const LS_SIZE_UK = "bootloop.sizeUk";
const LS_RADIUS = "bootloop.radiusMiles";
const LS_INCLUDE_NEXT = "bootloop.includeNextSize";
const LS_SHOE_TYPE = "bootloop.shoeType";
const LS_MIN_CONDITION = "bootloop.minCondition";
const LS_MY_INTERESTS = "bootloop.myInterests";

const API_BASE =
  (process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "") ||
  "https://localhost:7279";

function resolvePhotoUrl(u: string | null | undefined): string | null {
  const s = (u ?? "").trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("/")) return `${API_BASE}${s}`;
  return `${API_BASE}/${s}`;
}

function normEmail(v: string | null | undefined) {
  return (v ?? "").trim().toLowerCase();
}

function loadJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function saveJson(key: string, val: any) {
  try {
    localStorage.setItem(key, JSON.stringify(val));
  } catch {}
}

type MyInterestLocal = {
  listingId: number;
  interestId?: number;
  createdUtc: string;
  email: string;
};

function hasInterestLocal(listingId: number, email: string) {
  if (!email.trim()) return false;
  const all = loadJson<MyInterestLocal[]>(LS_MY_INTERESTS, []);
  return all.some(
    (x) =>
      x.listingId === listingId &&
      x.email.toLowerCase() === email.toLowerCase()
  );
}

function upsertInterestLocal(row: MyInterestLocal) {
  const all = loadJson<MyInterestLocal[]>(LS_MY_INTERESTS, []);
  const idx = all.findIndex(
    (x) =>
      x.listingId === row.listingId &&
      x.email.toLowerCase() === row.email.toLowerCase()
  );
  if (idx >= 0) all[idx] = { ...all[idx], ...row };
  else all.unshift(row);
  saveJson(LS_MY_INTERESTS, all.slice(0, 200));
}

function isValidSize(n: number) {
  return Number.isFinite(n) && n > 0 && n <= 15;
}

function readOptionalByteFromLs(key: string): "" | number {
  const raw = localStorage.getItem(key);
  if (raw == null) return "";
  const t = raw.trim();
  if (!t) return "";
  const n = Number(t);
  if (!Number.isFinite(n)) return "";
  return n;
}

/* ------------------------------------------------------------------
   Component
------------------------------------------------------------------ */

const FindSwapV2: React.FC = () => {
  const [email, setEmail] = useState(() => localStorage.getItem(LS_EMAIL) ?? "");
  const [postcode, setPostcode] = useState(
    () => localStorage.getItem(LS_POSTCODE) ?? ""
  );

  const [sizeUk, setSizeUk] = useState<number>(() => {
    const raw = localStorage.getItem(LS_SIZE_UK);
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) && n > 0 ? n : 3.5;
  });

  const [radiusMiles, setRadiusMiles] = useState<number>(() => {
    const raw = localStorage.getItem(LS_RADIUS);
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) && n > 0 ? n : 10;
  });

  const [includeNextSize, setIncludeNextSize] = useState<boolean>(() => {
    const raw = localStorage.getItem(LS_INCLUDE_NEXT);
    if (raw === "true") return true;
    if (raw === "false") return false;
    return true;
  });

  const [shoeType, setShoeType] = useState<ShoeType | "">(() => {
    const n = readOptionalByteFromLs(LS_SHOE_TYPE);
    return n === "" ? "" : (n as ShoeType);
  });

  const [minCondition, setMinCondition] = useState<BootCondition | "">(() => {
    const n = readOptionalByteFromLs(LS_MIN_CONDITION);
    return n === "" ? "" : (n as BootCondition);
  });

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [results, setResults] = useState<BootListingMatchDto[]>([]);
  const [loadedOnce, setLoadedOnce] = useState(false);

  /* Persist inputs */
  useEffect(() => localStorage.setItem(LS_EMAIL, email), [email]);
  useEffect(() => localStorage.setItem(LS_POSTCODE, postcode), [postcode]);
  useEffect(() => localStorage.setItem(LS_SIZE_UK, String(sizeUk)), [sizeUk]);
  useEffect(() => localStorage.setItem(LS_RADIUS, String(radiusMiles)), [radiusMiles]);
  useEffect(() => localStorage.setItem(LS_INCLUDE_NEXT, String(includeNextSize)), [includeNextSize]);
  useEffect(() => localStorage.setItem(LS_SHOE_TYPE, shoeType === "" ? "" : String(shoeType)), [shoeType]);
  useEffect(
    () => localStorage.setItem(LS_MIN_CONDITION, minCondition === "" ? "" : String(minCondition)),
    [minCondition]
  );

  async function load() {
    setErr(null);

    if (!postcode.trim()) {
      setErr("Please enter your postcode.");
      return;
    }
    if (!isValidSize(sizeUk)) {
      setErr("Please enter a valid size.");
      return;
    }

    try {
      setBusy(true);

      const rows = await bootloopClient.findMatches({
        postcode: postcode.trim(),
        sizeUk,
        radiusMiles,
        includeNextSize,
        shoeType: shoeType === "" ? undefined : shoeType,
        minCondition: minCondition === "" ? undefined : minCondition,
        maxRows: 60,
      });

      setResults(rows);
      setLoadedOnce(true);
    } catch (e: any) {
      setErr(e?.message || "Search failed.");
      setResults([]);
      setLoadedOnce(true);
    } finally {
      setBusy(false);
    }
  }

  async function sendInterest(listing: BootListingMatchDto) {
    const em = email.trim();
    if (!em) {
      setErr("Please enter your email.");
      return;
    }

    const ownerEmail = normEmail(listing.ownerEmail);
    const isMine = !!ownerEmail && ownerEmail === normEmail(em);
    if (isMine) {
      setErr("That is your own listing.");
      return;
    }

    try {
      setBusy(true);
      setErr(null);

      const res = await bootloopClient.createListingInterest(listing.listingId, {
        email: em,
        postcode: postcode.trim() || undefined,
      });

      upsertInterestLocal({
        listingId: listing.listingId,
        interestId: res?.interestId,
        createdUtc: new Date().toISOString(),
        email: em,
      });

      setResults((prev) => [...prev]);
    } catch (e: any) {
      setErr(e?.message || "Failed to send interest.");
    } finally {
      setBusy(false);
    }
  }

  const outward = postcode.trim()
    ? postcode.trim().split(/\s+/)[0].toUpperCase()
    : "";

  return (
    <div className="lp2app">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
        <div>
          <h1 className="lp2app__title">Find a swap</h1>
          <p className="lp2app__lead">
            Search by postcode and size. Send interest in one click.
          </p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <Link className="lp2app__btnSecondary" to="/list-boot">
            List boots
          </Link>
          <Link className="lp2app__btnSecondary" to="/my">
            My stuff
          </Link>
        </div>
      </div>

      {err && <div className="lp2app__alert">{err}</div>}

      <form
        className="lp2app__card lp2app__form"
        onSubmit={(e) => {
          e.preventDefault();
          load();
        }}
      >
        <div className="lp2app__row2">
          <label className="lp2app__label">
            Email
            <input
              className="lp2app__input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>

          <label className="lp2app__label">
            Postcode
            <input
              className="lp2app__input"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              placeholder="e.g. IP9 2FS"
            />
          </label>
        </div>

        <div className="lp2app__row2">
          <label className="lp2app__label">
            Size (UK)
            <input
              className="lp2app__input"
              type="number"
              step="0.5"
              min={1}
              max={15}
              value={sizeUk}
              onChange={(e) => setSizeUk(Number(e.target.value))}
            />
          </label>

          <label className="lp2app__label">
            Radius (miles)
            <input
              className="lp2app__input"
              type="number"
              min={1}
              max={100}
              value={radiusMiles}
              onChange={(e) => setRadiusMiles(Number(e.target.value))}
            />
          </label>
        </div>

        <div className="lp2app__row2">
          <label className="lp2app__label">
            Shoe type (optional)
            <select
              className="lp2app__select"
              value={shoeType === "" ? "" : String(shoeType)}
              onChange={(e) =>
                setShoeType(
                  e.target.value === "" ? "" : (Number(e.target.value) as ShoeType)
                )
              }
            >
              <option value="">Any</option>
              <option value="0">{shoeTypeLabel(0)}</option>
              <option value="1">{shoeTypeLabel(1)}</option>
              <option value="2">{shoeTypeLabel(2)}</option>
              <option value="3">{shoeTypeLabel(3)}</option>
              <option value="4">{shoeTypeLabel(4)}</option>
            </select>
          </label>

          <label className="lp2app__label">
            Min condition (optional)
            <select
              className="lp2app__select"
              value={minCondition === "" ? "" : String(minCondition)}
              onChange={(e) =>
                setMinCondition(
                  e.target.value === "" ? "" : (Number(e.target.value) as BootCondition)
                )
              }
            >
              <option value="">Any</option>
              <option value="0">{conditionLabel(0)}</option>
              <option value="1">{conditionLabel(1)}</option>
              <option value="2">{conditionLabel(2)}</option>
              <option value="3">{conditionLabel(3)}</option>
              <option value="4">{conditionLabel(4)}</option>
            </select>
          </label>
        </div>

        <div className="lp2app__row2">
          <label className="lp2app__label">
            Include next size
            <select
              className="lp2app__select"
              value={includeNextSize ? "true" : "false"}
              onChange={(e) => setIncludeNextSize(e.target.value === "true")}
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </label>

          <div className="lp2app__actions">
            <button className="lp2app__btnPrimary" type="submit" disabled={busy}>
              {busy ? "Searching…" : "Search"}
            </button>
          </div>
        </div>
      </form>

      {loadedOnce && (
        <div style={{ marginTop: 14, opacity: 0.9 }}>
          {results.length > 0 ? `Showing ${results.length} listings` : "No matches found."}
        </div>
      )}

      {results.length > 0 && (
        <div
          style={{
            marginTop: 12,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
            gap: 16,
            alignItems: "start",
          }}
        >
          {results.map((x) => {
            const imgSrc = resolvePhotoUrl(x.photoUrl);
            const already = hasInterestLocal(x.listingId, email.trim());

            return (
              <div
                key={x.listingId}
                style={{
                  borderRadius: 22,
                  overflow: "hidden",
                  border: "1px solid rgba(255,255,255,0.10)",
                  background: "rgba(255,255,255,0.04)",
                  boxShadow: "0 18px 40px rgba(0,0,0,0.35)",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div style={{ height: 140, overflow: "hidden" }}>
                  {imgSrc ? (
                    <img
                      src={imgSrc}
                      alt={`UK ${x.sizeUk}`}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                      loading="lazy"
                    />
                  ) : (
                    <div
                      style={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: 0.7,
                        fontWeight: 700,
                        fontSize: 12,
                      }}
                    >
                      No photo
                    </div>
                  )}
                </div>

                <div style={{ padding: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div style={{ fontWeight: 900, fontSize: 18 }}>UK {x.sizeUk}</div>

                    <div
                      style={{
                        padding: "4px 10px",
                        borderRadius: 999,
                        border: "1px solid rgba(84,255,170,0.35)",
                        background: "rgba(84,255,170,0.10)",
                        fontSize: 12,
                        fontWeight: 800,
                        whiteSpace: "nowrap",
                        alignSelf: "center",
                      }}
                    >
                      {conditionLabel(Number(x.condition))}
                    </div>
                  </div>

                  <div style={{ marginTop: 6, opacity: 0.85, fontSize: 13 }}>
                    {shoeTypeLabel(Number(x.shoeType))} · {outward}
                  </div>

                  <div style={{ marginTop: 6, opacity: 0.85, fontSize: 13 }}>
                    Distance:{" "}
                    {x.distanceMiles != null
                      ? `${Number(x.distanceMiles).toFixed(1)} miles`
                      : "—"}
                  </div>

                  <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center" }}>
                    <button
                      type="button"
                      onClick={() => sendInterest(x)}
                      disabled={busy || already}
                      style={{
                        flex: 1,
                        padding: "10px 14px",
                        borderRadius: 999,
                        border: "none",
                        color: "#111",
                        fontWeight: 800,
                        cursor: busy || already ? "default" : "pointer",
                        background:
                          busy || already
                            ? "rgba(255,255,255,0.22)"
                            : "linear-gradient(90deg,#7b4bff,#ff4bbd)",
                      }}
                    >
                      {already ? "Interest sent ✓" : "I'm interested"}
                    </button>

                    {/* ✅ PUBLIC VIEW MODE */}
                    <Link
                      to={`/boots/${x.listingId}?public=1&back=/find-swap-v2`}
                      style={{
                        padding: "10px 14px",
                        borderRadius: 999,
                        border: "1px solid rgba(255,255,255,0.30)",
                        textDecoration: "none",
                        color: "white",
                        fontWeight: 800,
                        whiteSpace: "nowrap",
                      }}
                    >
                      View
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FindSwapV2;